import { Fragment, useState, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { X, Send } from 'lucide-react'
import { motion } from 'framer-motion'

interface ContactModalProps {
  isOpen: boolean
  onClose: () => void
  serviceType?: string
}

// Helper function to get CSRF token from meta tag
function getCsrfToken(): string | null {
  const metaTag = document.querySelector('meta[name="csrf-token"]')
  return metaTag ? metaTag.getAttribute('content') : null
}

export default function ContactModal({ isOpen, onClose, serviceType = 'ТГ бот - заявка' }: ContactModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    description: '',
    politics: false,
    service: serviceType
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  // Sync service value with prop when modal opens or serviceType changes
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({ ...prev, service: serviceType }))
    }
  }, [serviceType, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    
    if (!formData.politics) {
      alert('Пожалуйста, подтвердите согласие на обработку персональных данных')
      return
    }

    setIsSubmitting(true)

    try {
      const csrfToken = getCsrfToken()
      
      if (!csrfToken) {
        throw new Error('CSRF token not found. Please ensure the meta tag is present in your HTML.')
      }

      // Create FormData to match Laravel's expected format
      const submitData = new FormData()
      submitData.append('_token', csrfToken)
      submitData.append('name', formData.name)
      submitData.append('phone', formData.phone)
      submitData.append('description', formData.description)
      submitData.append('politics', formData.politics ? 'on' : '0')
      submitData.append('service', formData.service)

      // Submit to Laravel backend
      const response = await fetch('/orders', {
        method: 'POST',
        body: submitData,
        credentials: 'same-origin', // Include cookies for session
        headers: {
          'X-CSRF-TOKEN': csrfToken,
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        }
      })

      if (!response.ok) {
        // Handle validation errors from Laravel
        if (response.status === 422) {
          const errorData = await response.json()
          if (errorData.errors) {
            setErrors(errorData.errors)
            // Show first error as alert
            const firstError = Object.values(errorData.errors)[0]
            if (Array.isArray(firstError) && firstError.length > 0) {
              alert(firstError[0])
            }
            return
          }
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      // Success
      alert('Спасибо за заявку! Мы свяжемся с вами в ближайшее время.')
      onClose()
      setFormData({ name: '', phone: '', description: '', politics: false, service: serviceType })
      
    } catch (error) {
      console.error('Form submission error:', error)
      alert('Произошла ошибка при отправке формы. Пожалуйста, попробуйте позже.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    })
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6 text-white">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-2xl font-bold">
                      Оставить заявку
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="rounded-full p-2 hover:bg-white/20 transition-colors"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                  <p className="mt-2 text-blue-100">
                    Ответим в течение 1 рабочего дня
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-8">
                  <div className="mb-6">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Ваше имя *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all ${
                        errors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Иван Иванов"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name[0]}</p>
                    )}
                  </div>

                  <div className="mb-6">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Телефон *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all ${
                        errors.phone ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="+7 (999) 123-45-67"
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-600">{errors.phone[0]}</p>
                    )}
                  </div>

                  <div className="mb-6">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                      Расскажите о вашем проекте
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      rows={4}
                      value={formData.description}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none ${
                        errors.description ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Кратко опишите задачу, которую хотите автоматизировать..."
                    />
                    {errors.description && (
                      <p className="mt-1 text-sm text-red-600">{errors.description[0]}</p>
                    )}
                  </div>

                  {/* Privacy Consent Checkbox */}
                  <div className="mb-6">
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        name="politics"
                        checked={formData.politics}
                        onChange={handleChange}
                        required
                        className={`mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer ${
                          errors.politics ? 'border-red-500' : ''
                        }`}
                      />
                      <span className="text-sm text-gray-700 leading-relaxed">
                        Согласен(-а) на обработку данных в соответствии с{' '}
                        <a
                          href="https://softrest.ru/politics"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 underline font-medium"
                        >
                          политикой конфиденциальности
                        </a>
                        . *
                      </span>
                    </label>
                    {errors.politics && (
                      <p className="mt-1 text-sm text-red-600">{errors.politics[0]}</p>
                    )}
                  </div>

                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                    whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                    className={`w-full flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold transition-all shadow-lg ${
                      isSubmitting
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:from-blue-700 hover:to-indigo-700 hover:shadow-xl'
                    }`}
                  >
                    <Send className="w-5 h-5" />
                    {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
                  </motion.button>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
